/*
 * @stayai — Upcart cart-drawer "subscription upgrade" plan filter.
 *
 * Problem: each cart line's subscription-upgrade <select> (inside Upcart's product tile)
 * enumerates a product's selling_plan_groups across ALL subscription apps (Stay 5859381 +
 * Skio SKIO), with no app_id filter. A shopper can pick the wrong app's plan straight from
 * the cart drawer (§5c cart-payload spill). Upcart is React, mounted in an OPEN shadow root
 * (#upCart), unreachable from Liquid — client-side lever, same shape as Bridgehead's 
 *
 * What it does: whenever a subscription-upgrade <select> appears in the drawer shadow root,
 * remove every <option> whose plan's app_id != ACTIVE_APP_ID. Keeps any unknown/uncached plan
 * (never blanks the UI on a partial fetch). If the selected option gets removed, resets to a
 * safe active-app default and dispatches change via the React value setter so Upcart's own
 * state re-syncs. If a line's product has NO active-app plan at all, hides that line's
 * subscription-upgrade control entirely rather than leaving an empty <select>.
 *
 * Active app id: single source snippets/active-subscription-app-id.liquid, passed in as
 * window.STAYAI_ACTIVE_APP_ID from theme.liquid. Flip the app there, not here.
 *
 * LIVE-VERIFIED 2026-08-06 — shadow root confirmed open, and the drawer's subscription-upgrade
 * select was confirmed showing only the active app's selling-plan-group options on a real cart.
 */
(function () {
  "use strict";

  var ACTIVE_APP_ID = String(window.STAYAI_ACTIVE_APP_ID || "").trim();
  if (!ACTIVE_APP_ID) return; // no toggle value -> do nothing

  var DEBUG = window.STAYAI_UPCART_DEBUG || false;
  function log() { if (DEBUG) console.log.apply(console, [">>> [upcart-filter]"].concat([].slice.call(arguments))); }

  var DRAWER_HOST_ID = "upCart";
  var SELECT_SEL = ".upcart-internal-component-product-tile__select";
  var WRAPPER_SEL = ".upcart-internal-component-product-tile__subscription-upgrade";

  // ---- plan_id -> app_id map, built from /cart.js handles -> /products/<handle>.js ----
  var planAppId = Object.create(null);      // { "<selling_plan_id>": "<app_id>" }
  var productCache = Object.create(null);   // handle -> Promise (fetch once)
  var lastCartFetch = 0;

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch("/products/" + handle + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p && p.selling_plan_groups) {
          p.selling_plan_groups.forEach(function (g) {
            (g.selling_plans || []).forEach(function (sp) { planAppId[String(sp.id)] = String(g.app_id); });
          });
        }
      })
      .catch(function () {}); // degrade to show-all on failure
    return productCache[handle];
  }

  function refreshMap() {
    lastCartFetch = Date.now();
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var handles = {};
        cart.items.forEach(function (it) { if (it.handle) handles[it.handle] = true; });
        return Promise.all(Object.keys(handles).map(fetchProduct));
      })
      .catch(function () {});
  }

  // React-safe value setter (a plain el.value / native change won't update React state)
  function setNativeValue(el, value) {
    var desc = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value");
    if (desc && desc.set) desc.set.call(el, value); else el.value = value;
  }

  // Strip foreign options from a single <select>; repair selection if needed;
  // hide the whole subscription-upgrade control if nothing active-app-eligible remains.
  function filterSelect(select) {
    if (select.dataset.stayFiltered === "1") return;
    var before = select.options.length;
    var removedSelected = false;

    [].slice.call(select.options).forEach(function (opt) {
      var appId = planAppId[opt.value];
      if (!appId) return; // unknown -> keep (never blank the UI)
      if (appId !== ACTIVE_APP_ID) {
        if (opt.selected) removedSelected = true;
        opt.remove();
      }
    });

    // Drop now-empty <optgroup>s so the dropdown never shows a bare group header.
    [].slice.call(select.querySelectorAll("optgroup")).forEach(function (og) {
      if (!og.querySelector("option")) og.remove();
    });

    if (!select.options.length) {
      var wrapper = select.closest(WRAPPER_SEL);
      if (wrapper) wrapper.style.display = "none";
      log("no active-app plan left for this line; hid subscription-upgrade control");
      select.dataset.stayFiltered = "1";
      return;
    }

    if (removedSelected) {
      setNativeValue(select, select.options[0].value);
      select.dispatchEvent(new Event("change", { bubbles: true }));
      log("removed foreign selected plan; reset to", select.options[0].value);
    }

    if (before !== select.options.length) log("stripped", before - select.options.length, "foreign option(s)");
    select.dataset.stayFiltered = "1";
  }

  // Find subscription-upgrade selects, descending into any (open) nested shadow roots.
  function deepSelects(root, acc) {
    acc = acc || [];
    acc.push.apply(acc, root.querySelectorAll(SELECT_SEL));
    root.querySelectorAll("*").forEach(function (el) { if (el.shadowRoot) deepSelects(el.shadowRoot, acc); });
    return acc;
  }

  function runFilter(shadow) {
    var selects = deepSelects(shadow).filter(function (s) { return s.dataset.stayFiltered !== "1"; });
    if (!selects.length) return;
    var hasUnknown = selects.some(function (s) {
      return [].some.call(s.options, function (o) { return !(o.value in planAppId); });
    });
    if (hasUnknown && Date.now() - lastCartFetch > 1500) {
      refreshMap().then(function () { selects.forEach(filterSelect); });
    } else {
      selects.forEach(filterSelect);
    }
  }

  var observer = null; // set once the drawer exists; null means "not observing"

  function attach(host) {
    var shadow = host && host.shadowRoot;
    if (!shadow) { log("drawer host has no open shadowRoot (closed root, or not yet attached)"); return false; }
    if (observer) return true;
    var debounce;
    observer = new MutationObserver(function () {
      if (!host.isConnected) { // drawer host removed from DOM -> stop observing, allow re-attach
        observer.disconnect();
        observer = null;
        log("drawer host removed; observer disconnected");
        return;
      }
      clearTimeout(debounce);
      debounce = setTimeout(function () { runFilter(shadow); }, 30);
    });
    observer.observe(shadow, { childList: true, subtree: true });
    runFilter(shadow); // initial pass
    log("attached to drawer shadow root (ACTIVE_APP_ID=" + ACTIVE_APP_ID + ")");
    return true;
  }

  function tryAttach() {
    return attach(document.getElementById(DRAWER_HOST_ID));
  }

  // Upcart mounts async. Poll for its host + shadow root, attach once, then stop.
  // If no Upcart drawer ever appears (app not on this page), give up cleanly and never
  // start a MutationObserver.
  function init() {
    if (tryAttach()) return;
    var tries = 0;
    var poll = setInterval(function () {
      if (tryAttach()) {
        clearInterval(poll);
      } else if (++tries > 40) { // ~20s self-terminate
        clearInterval(poll);
        log("no Upcart drawer found (~20s); not observing");
      }
    }, 500);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
